// React
import * as React from "react";

import ReactPlayer from "react-player";
import { CembooPlayer } from "@haydenfilms-institute/cemboo-player";

// Amplify UI
import {
    Flex,
    Loader,
    SelectField,
    View,
    useTheme,
} from "@aws-amplify/ui-react";
import { adServerService, configService, mediaService, filesService } from "../../services";
import { IMedia, IMediaRating, IMemberPlan, IPlaylistResponseDto, IUploadCategory, IVODAttachments, PlaylistTemplate } from "../../types";
import { VODAttachmentsMenu } from "../vod-attachments";
import { SocialShare } from "../social-share";
import * as Styled from "./MediaCollection.styled";
import { WebAnalyticsContext } from "../../context";
import "./media-collection.css";
import { IRollAd, IRollAdVideo, IAdView } from "../../types";
import RatingToast from "../layouts/RatingToast";
import { Layout1 } from "../layouts";
import { Layout1 as MobileVodLayout } from "../layouts-mobile/Layout1";
import { templateComponentMap } from "../../libs/utils/templateComponentMap";
import { useIsMobile, useScrollLock } from "../hooks";
import * as Dialog from "@radix-ui/react-dialog";
import { XLg } from "react-bootstrap-icons";


export const MediaCollection: React.FC<{
    clientId: string;
    fetchByDate: string;
    enableFilterControls: boolean;
    enableRatingControls: boolean;
    enableShareControls: boolean;
    sessionToken: string;
    isSignedIn: boolean;
    mediaPerPage: number;
    enableAutoplay: boolean;
    existingMedia?: IMedia[];
    externalLoader?: boolean;
    enableCompetitionMode?: boolean;
    playlist?: IPlaylistResponseDto; // This will signify if this collection is used for playlist rendering
}> = ({
    clientId,
    fetchByDate,
    enableFilterControls,
    enableRatingControls,
    enableShareControls,
    sessionToken,
    isSignedIn,
    mediaPerPage,
    enableAutoplay,
    existingMedia,
    externalLoader,
    playlist,
    enableCompetitionMode
}) => {
        const { tokens } = useTheme();
        const isMobile = useIsMobile();
        const [showLoader, setShowLoader] = React.useState(true);

        const [media, setMedia] = React.useState<IMedia[]>([]);
        const [mediaGenres, setMediaGenres] = React.useState<string[]>([]);
        const [mediaRating, setMediaRating] = React.useState<IMediaRating[]>([]);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [currentPlayingMediaItem, setCurrentPlayingMediaItem] =
            React.useState<IMedia | undefined>(undefined);

        const [showPlayerModal, setShowPlayerModal] = React.useState(false);
        useScrollLock(showPlayerModal);
        const [showUpdateToast, setShowUpdateToast] = React.useState(false);
        const [useCembooPlayer, setUseCembooPlayer] = React.useState(true);
        const [hasFullAccess, setHasFullAccess] = React.useState(false);
        const [freeTrialPlanIds, setFreeTrialPlanIds] = React.useState<string[]>(
            []
        );

        const [activePlans, setActivePlans] = React.useState<IMemberPlan[]>([]);
        const [vodAttachments, setVodAttachments] = React.useState<IVODAttachments>({ files: [], hyperlinks: [] });
        const [selectedGenre, setSelectedGenre] = React.useState("");

        const [rollAds, setRollAds] = React.useState<IRollAd[]>([]);
        const [adMode, setAdMode] = React.useState(false);
        const [adVideo, setAdVideo] = React.useState<IRollAdVideo>();
        const [currentAd, setCurrentAd] = React.useState<IRollAdVideo>();

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [generalVodTemplateId, setGeneralVodTemplateId] = React.useState<number>(0);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [filmFestivalVodTemplateId, setFilmFestivalVodTemplateId] = React.useState<number>(0);

        const { updateVideoWebAnalyticsData, setSessionToken } =
            React.useContext(WebAnalyticsContext);
        const [playerAnalyticsData, setPlayerAnalyticsData] =
            React.useState<any>(null);

        const getAllMediaRatings = React.useCallback(async () => {
            setSessionToken(sessionToken);
            try {
                const ratings = await mediaService.getAllMediaRatings(sessionToken);
                setMediaRating(ratings);
            } catch (err) {
                console.log(err);
            }
        }, [sessionToken, setSessionToken]);

        const getAllMedia = React.useCallback(async () => {
            try {
                var result = [];
                if (existingMedia) {
                    result = existingMedia;
                    setMedia(existingMedia);
                    setShowLoader(false);
                } else {
                    result = await mediaService.getAllMedia(clientId, fetchByDate);
                    setMedia(shuffleArray(result));

                    const vodTemplates = await configService.getLibraryTemplates(clientId);
                    console.log(vodTemplates)
                }

                const rollAdsResponse = await adServerService.getAllRollAds(clientId);
                setRollAds(rollAdsResponse);

                if (result.length) {
                    // get available genres directly from media (exclude undefined)
                    const tmpGenres: any[] = [];
                    result.map((item) => {
                        if (item.genre) {
                            if (item.genre.includes(",")) {
                                let _tmpArr = item.genre.split(",");
                                tmpGenres.push(..._tmpArr);
                                return true;
                            } else {
                                tmpGenres.push(item.genre.trim());
                                return true;
                            }
                        } else {
                            return false;
                        }
                    });
                    const uniqueGenres: any[] = [
                        ...new Set(
                            tmpGenres
                                .filter((item) => {
                                    return item !== undefined;
                                })
                                .sort()
                        ),
                    ];
                    setMediaGenres(uniqueGenres);
                }
            } catch (err) {
                console.log(err);
            } finally {
                setShowLoader(false);
            }
        }, [
            clientId,
            fetchByDate,
            existingMedia,
            setMedia,
            setMediaGenres,
            setShowLoader,
        ]);

        const shuffleArray = (arr: any) => {
            let tmpArr;
            for (let i = arr.length - 1; i > 0; i--) {
                let rand = Math.floor(Math.random() * (i + 1));
                tmpArr = arr[i];
                arr[i] = arr[rand];
                arr[rand] = tmpArr;
            }
            return arr;
        };

        const filteredMedia = media.filter(
            (item) => selectedGenre === "" || item.genre.includes(selectedGenre)
        );

        const fetchCustomerActivePlans = React.useCallback(async () => {
            const result = await mediaService.getCustomerPurchasedPlans(
                sessionToken!
            );

            try {
                const trailData = await mediaService.checkTrialCodeUser(
                    sessionToken!
                );
                if (trailData.length > 0) {
                    setHasFullAccess(trailData[0].isFullAccess);
                    setFreeTrialPlanIds(trailData[0].planId);
                }
            } catch (err) {
                console.log(err);
            }

            setActivePlans(
                result.filter((item) =>
                    item.expiryDate
                        ? new Date(item.expiryDate).getTime() > Date.now()
                        : true
                )
            );
        }, [sessionToken]);

        const insertMediaActivity = React.useCallback(() => {
            const anonId = localStorage.getItem("anonId");
            const mediaId = currentPlayingMediaItem?.mediaId;
            if (playerAnalyticsData && !showPlayerModal) {
                if (playerAnalyticsData.watchTime > 3) {
                    updateVideoWebAnalyticsData({
                        anonId: anonId || "",
                        mediaId: mediaId || "",
                        rewindsSkips:
                            playerAnalyticsData.rewinds + playerAnalyticsData.skips,
                        engagement: playerAnalyticsData.engagement,
                        watchTime: playerAnalyticsData.watchTime,
                        isCompleted: playerAnalyticsData.isCompleted,
                    });
                }

                setPlayerAnalyticsData(null);
            }
        }, [
            updateVideoWebAnalyticsData,
            showPlayerModal,
            currentPlayingMediaItem,
            playerAnalyticsData,
        ]);

        React.useEffect(() => {
            window.addEventListener("beforeunload", () => {
                setShowPlayerModal(false);
                insertMediaActivity();
            });

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        React.useEffect(() => {
            window.addEventListener("onFetchUserPlansEvent", () => {
                if (sessionToken) {
                    fetchCustomerActivePlans();
                }
            });
        }, [fetchCustomerActivePlans, sessionToken]);

        React.useEffect(() => {
            if (isSignedIn && sessionToken) {
                fetchCustomerActivePlans();
                setSessionToken(sessionToken);
            }
        }, [fetchCustomerActivePlans, setSessionToken, isSignedIn, sessionToken]);

        const generateLoadSignInModalEvent = React.useCallback(
            (event: IMedia, showPurchasePlanModal?: boolean) => {
                const displayLoginEvent = new CustomEvent("loadSignInModal", {
                    detail: {
                        eventRequested: event,
                        showPurchasePlanModal: showPurchasePlanModal,
                        requestedPlanType: 1,
                    },
                });

                window.dispatchEvent(displayLoginEvent);
            },
            []
        );

        const adShouldShow = React.useCallback((adType: "pre-roll" | "mid-roll" | "post-roll") => {
            try {
                const items: IAdView[] = JSON.parse(localStorage.getItem("adViews") || "[]");
                const now = Date.now();

                const TWO_MIN = 2 * 60 * 1000;
                const TEN_MIN = 10 * 60 * 1000;
                const SIXTY_MIN = 60 * 60 * 1000;

                const relevantViews = items.filter(item => item.adType === adType);

                const last2Min = relevantViews.filter(item => now - item.timestamp <= TWO_MIN);
                const last10Min = relevantViews.filter(item => now - item.timestamp <= TEN_MIN);
                const last60Min = relevantViews.filter(item => now - item.timestamp <= SIXTY_MIN);

                if (
                    last2Min.length === 0 &&
                    last10Min.length < 3 &&
                    last60Min.length < 10
                ) {
                    return true;
                }

            } catch (err) {
                console.log("Failed to check ad display eligibility:", err);
            }

            return false;
        }, []);


        const handleWatchNowClick = React.useCallback(
            async (item: IMedia) => {
                if (isSignedIn) {
                    //console.log('item', item);
                    //console.log('item.planDetails', item.planDetails);
                    const hasAccess = !!item.planDetails.length
                        ? !!activePlans.filter((memberPlan) =>
                            item.planDetails?.some((itemPlan) => {
                                return itemPlan?.id === memberPlan.id;
                            })
                        ).length ||
                        freeTrialPlanIds.includes(item.planDetails[0].id) ||
                        hasFullAccess
                        : true;

                    console.log("Has Access: ", hasAccess);

                    if (hasAccess) {
                        try {
                            setShowPlayerModal(true);
                            setCurrentPlayingMediaItem(item);
                        } catch (err) {
                            console.log("Error while fetching token is: ", err);
                        }
                    } else {
                        generateLoadSignInModalEvent(item, true);
                    }
                } else {
                    if (!!item.planDetails.length) {
                        generateLoadSignInModalEvent(item);
                    } else {
                        setShowPlayerModal(true);
                        var preRollAds: IRollAd[] = []
                        if (playlist) {
                            preRollAds = rollAds.filter(
                                (ad) => ad.preRollAds.length > 0 && (ad.targetPlaylists.includes(playlist.playlistId) || ad.targetVideos.includes(playlist.playlistId))
                            );
                        } else {
                            preRollAds = rollAds.filter(
                                (ad) => ad.preRollAds.length > 0 && (ad.targetPlaylists.includes(item.id) || ad.targetVideos.includes(item.id))
                            );
                        }
                        if (preRollAds.length > 0 && adShouldShow("pre-roll")) {
                            setAdVideo(preRollAds[0].preRollAds[0]);
                            setAdMode(true);
                            setCurrentAd(preRollAds[0].preRollAds[0])
                        }
                        setCurrentPlayingMediaItem(item);
                        filesService.getVODAttachments(item.mediaId).then((attachments) => {
                            setVodAttachments(attachments);
                        });
                    }
                }
            },
            [activePlans, isSignedIn, freeTrialPlanIds, hasFullAccess, generateLoadSignInModalEvent, rollAds, adShouldShow, playlist]
        );

        const toggleShowUpdateToast = () => {
            setShowUpdateToast(!showUpdateToast);
        };

        const handleRatingSuccess = () => {
            setShowUpdateToast(true);
            getAllMediaRatings();
        };

        const handleGenreFilterChange = (evt: any) => {
            setSelectedGenre(evt.target.value);
            evt.target.blur();
        };

        React.useEffect(() => {
            getAllMedia();

            if (enableRatingControls && isSignedIn) {
                getAllMediaRatings();
            }
        }, [isSignedIn, enableRatingControls, getAllMedia, getAllMediaRatings]);

        const handleSwitchPlayer = React.useCallback(() => {
            setUseCembooPlayer(prev => !prev);
        }, []);

        React.useEffect(() => {
            insertMediaActivity();
        }, [showPlayerModal, insertMediaActivity]);

        const handlePlayerAnalytics = React.useCallback((data: any) => {
            console.log("Player Analytics: ", data);
            setPlayerAnalyticsData(data);
        }, []);

        React.useEffect(() => {
            if (externalLoader === true) {
                setShowLoader(true);
            }
            if (externalLoader === false) {
                setShowLoader(false);
            }
        }, [externalLoader]);

        const adDidComplete = React.useCallback(() => {
            var adViewsStr = localStorage.getItem("adViews")
            var adViews: IAdView[] = []

            try {
                adViews = adViewsStr ? JSON.parse(adViewsStr) : []
            } catch (err) {
                console.error(err)
            }

            adViews = adViews.filter((element) => {
                return (Date.now() - element.timestamp) <= 10 * 60 * 1000;
            })

            adViews.push({
                adType: currentAd?.adType || "",
                timestamp: Date.now()
            })

            localStorage.setItem("adViews", JSON.stringify(adViews));

            setAdMode(false);
            setAdVideo(undefined);
            setCurrentAd(undefined);

        }, [currentAd]);

        const VodTemplateComponent = isMobile ? MobileVodLayout : (templateComponentMap[generalVodTemplateId as PlaylistTemplate] || Layout1);
        const FilmFestivalVodTemplateComponent = isMobile ? MobileVodLayout : (templateComponentMap[filmFestivalVodTemplateId as PlaylistTemplate] || Layout1);

        if (showLoader) {
            return (
                <Flex justifyContent={"center"} alignItems={"center"}>
                    <View padding={tokens.space.large}>
                        <Loader size="large" />
                    </View>
                </Flex>
            );
        }

        return (
            <div className="cemboo-collection--wrapper position-relative" data-cemboo="media-collection">
                <div className="collection-controls d-flex flex-nowrap justify-content-end align-items-center gap-4" data-cemboo="media-collection-controls">
                    {enableShareControls && (
                        <div
                            className="collection-controls--share d-flex align-items-center mb-3"
                            data-cemboo="media-collection-share"
                            style={{
                                fontSize: "12px",
                                textTransform: "uppercase",
                            }}
                        >
                            <SocialShare />
                        </div>
                    )}

                    {enableFilterControls && (
                        <div
                            className="collection-controls--filters d-flex align-items-center mb-3"
                            data-cemboo="media-collection-filters"
                            style={{ gap: "8px" }}
                        >
                            <SelectField
                                label="FILTER BY GENRE:"
                                value={selectedGenre}
                                onChange={handleGenreFilterChange}
                                size="small"
                                direction="row"
                                alignItems="center"
                                gap="0"
                                className="cemboo-genre-filter"
                            >
                                <option value="">All</option>
                                {mediaGenres.map((genre, idx) => (
                                    <option value={genre} key={idx}>
                                        {genre}
                                    </option>
                                ))}
                            </SelectField>
                        </div>
                    )}
                </div>


                {playlist ? (
                    <VodTemplateComponent
                        playlistTitle={playlist.title}
                        mediaItems={filteredMedia}
                        mediaRating={mediaRating}
                        onWatchClick={handleWatchNowClick}
                        enableRatingControls={enableRatingControls}
                        isSignedIn={isSignedIn}
                        hasFullAccess={hasFullAccess}
                        activePlans={activePlans}
                        freeTrialPlanIds={freeTrialPlanIds}
                        sessionToken={sessionToken}
                        enableCompetitionMode={enableCompetitionMode}
                        onRatingSuccess={handleRatingSuccess}
                        mediaPerPage={mediaPerPage}
                    />
                ) : (
                    <>
                        <VodTemplateComponent
                            playlistTitle="VOD Collection"
                            mediaItems={filteredMedia.filter(item => item.category === IUploadCategory.GENERAL)}
                            mediaRating={mediaRating}
                            onWatchClick={handleWatchNowClick}
                            enableRatingControls={enableRatingControls}
                            isSignedIn={isSignedIn}
                            hasFullAccess={hasFullAccess}
                            activePlans={activePlans}
                            freeTrialPlanIds={freeTrialPlanIds}
                            sessionToken={sessionToken}
                            enableCompetitionMode={enableCompetitionMode}
                            onRatingSuccess={handleRatingSuccess}
                            mediaPerPage={mediaPerPage}
                        />
                        <FilmFestivalVodTemplateComponent
                            playlistTitle="Film Festival VOD Collection"
                            mediaItems={filteredMedia.filter(item => item.category === IUploadCategory.FILM_FESTIVAL)}
                            mediaRating={mediaRating}
                            onWatchClick={handleWatchNowClick}
                            enableRatingControls={enableRatingControls}
                            isSignedIn={isSignedIn}
                            hasFullAccess={hasFullAccess}
                            activePlans={activePlans}
                            freeTrialPlanIds={freeTrialPlanIds}
                            sessionToken={sessionToken}
                            enableCompetitionMode={enableCompetitionMode}
                            onRatingSuccess={handleRatingSuccess}
                            mediaPerPage={mediaPerPage}
                        />
                    </>
                )}


                <Dialog.Root
                    open={showPlayerModal}
                    onOpenChange={(open: boolean) => !open && setShowPlayerModal(false)}
                    modal={false}
                >
                    <Dialog.Portal>
                        <Styled.DialogOverlay data-cemboo="media-collection-modal-overlay" />
                        <Styled.DialogContent
                            data-cemboo="media-collection-modal-content"
                            className="cemboo-video-modal"
                        >
                            <Styled.ModalHeader data-cemboo="media-collection-modal-header">
                                <Flex
                                    justifyContent={"space-between"}
                                    alignItems={"center"}
                                    width="100%"
                                >
                                    <Styled.ModalTitle data-cemboo="media-collection-modal-title">
                                        {adMode ? "Advertisement" : currentPlayingMediaItem?.title}
                                    </Styled.ModalTitle>
                                    <Flex alignItems={"center"} gap="8px">
                                        <Styled.HeaderButton onClick={handleSwitchPlayer}>
                                            Switch Player
                                        </Styled.HeaderButton>
                                        <Styled.CloseButton data-cemboo="media-collection-modal-close" aria-label="Close">
                                            <XLg />
                                        </Styled.CloseButton>
                                    </Flex>
                                </Flex>
                            </Styled.ModalHeader>
                            <Styled.ModalBody data-cemboo="media-collection-modal-body">
                                {useCembooPlayer ? (
                                    <CembooPlayer
                                        url={
                                            adMode && adVideo?.videoUrl ? adVideo.videoUrl :
                                                currentPlayingMediaItem
                                                    ? currentPlayingMediaItem.video
                                                    : ""
                                        }
                                        showThumbnail={true}
                                        onVideoAnalyticsUpdate={handlePlayerAnalytics}
                                        adMode={adMode}
                                        adConfig={{
                                            onAdCompleted: adDidComplete
                                        }}
                                        playing={enableAutoplay}
                                        menuComponent={<VODAttachmentsMenu attachments={vodAttachments} />}
                                    />
                                ) : (
                                    <ReactPlayer
                                        url={currentPlayingMediaItem?.video}
                                        width={"100%"}
                                        height={"100%"}
                                        controls
                                    />
                                )}
                            </Styled.ModalBody>
                        </Styled.DialogContent>
                    </Dialog.Portal>
                </Dialog.Root>

                <RatingToast show={showUpdateToast} onClose={toggleShowUpdateToast} />
            </div>
        );
    };
